<!--
/**
 * OrangeHRM is a comprehensive Human Resource Management (HRM) System that captures
 * all the essential functionalities required for any enterprise.
 * Copyright (C) 2006 OrangeHRM Inc., http://www.orangehrm.com
 *
 * OrangeHRM is free software; you can redistribute it and/or modify it under the terms of
 * the GNU General Public License as published by the Free Software Foundation; either
 * version 2 of the License, or (at your option) any later version.
 *
 * OrangeHRM is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
 * without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
 * See the GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License along with this program;
 * if not, write to the Free Software Foundation, Inc., 51 Franklin Street, Fifth Floor,
 * Boston, MA  02110-1301, USA
 */
 -->

<template>
  <div
    v-if="openSessionGuidance"
    class="orangehrm-attendance-open-session-banner"
  >
    <div
      class="orangehrm-attendance-open-session-alert"
      :class="
        openSessionGuidance.durationBlocked
          ? 'orangehrm-attendance-open-session-alert--error'
          : 'orangehrm-attendance-open-session-alert--warn'
      "
      role="alert"
    >
      <oxd-icon
        :name="
          openSessionGuidance.durationBlocked
            ? 'exclamation-circle'
            : 'exclamation-triangle'
        "
        class="orangehrm-attendance-open-session-alert-icon"
      />
      <div class="orangehrm-attendance-open-session-alert-content">
        <oxd-text tag="p" class="orangehrm-attendance-open-session-title">
          {{
            openSessionGuidance.durationBlocked
              ? $t('attendance.open_session_duration_blocked_title')
              : $t('attendance.open_session_blocks_clock_in_title')
          }}
        </oxd-text>
        <oxd-text
          v-if="openSessionGuidance.durationBlocked"
          tag="p"
          class="orangehrm-attendance-open-session-body"
        >
          {{
            $t('attendance.open_session_duration_blocked_message', {
              punchInAt: openSessionGuidance.punchInAt,
            })
          }}
        </oxd-text>
        <oxd-text v-else tag="p" class="orangehrm-attendance-open-session-body">
          {{
            $t('attendance.open_session_blocks_clock_in_body', {
              punchInAt: openSessionGuidance.punchInAt,
            })
          }}
        </oxd-text>
        <div
          v-if="openSessionGuidance.durationBlocked"
          class="orangehrm-attendance-open-session-actions"
        >
          <oxd-button
            display-type="secondary"
            :label="$t('attendance.open_session_bypass_action')"
            :loading="isClosingStaleSession"
            @click="onBypassToClockIn"
          />
          <oxd-button
            display-type="ghost"
            :label="$t('attendance.open_session_adjustment_action')"
            @click="onOpenSessionAdjustment"
          />
        </div>
      </div>
    </div>
    <oxd-divider />
  </div>
  <confirmation-dialog
    ref="bypassConfirmDialog"
    :title="bypassConfirmTitle"
    :subtitle="bypassConfirmMessage"
    :cancel-label="$t('general.no_cancel')"
    :confirm-label="$t('attendance.open_session_bypass_action')"
    icon="exclamation-circle"
    confirm-button-type="secondary"
  />
  <div
    v-if="isOutsideWorkWeek"
    class="orangehrm-attendance-open-session-banner"
  >
    <div
      class="orangehrm-attendance-open-session-alert orangehrm-attendance-open-session-alert--warn"
      role="status"
    >
      <oxd-icon
        name="exclamation-triangle"
        class="orangehrm-attendance-open-session-alert-icon"
      />
      <div class="orangehrm-attendance-open-session-alert-content">
        <oxd-text tag="p" class="orangehrm-attendance-open-session-body">
          {{ $t('attendance.not_scheduled_work_day') }}
        </oxd-text>
      </div>
    </div>
  </div>
  <oxd-form :loading="isLoading" @submit-valid="getLocation">
    <oxd-form-row>
      <oxd-grid :cols="4" class="orangehrm-full-width-grid">
        <template v-if="attendanceRecord.previousRecord">
          <oxd-grid-item
            :class="
              !attendanceRecord.previousRecord.note ? '--span-column-2' : ''
            "
          >
            <oxd-input-group :label="$t('attendance.punched_in_time')">
              <oxd-text type="subtitle-2">
                {{ previousAttendanceRecordDate }} -
                {{ previousAttendanceRecordTime }}
                <oxd-text
                  tag="span"
                  class="orangehrm-attendance-punchedIn-timezone"
                >
                  {{ `(UTC ${previousRecordTimezone})` }}
                </oxd-text>
              </oxd-text>
            </oxd-input-group>
          </oxd-grid-item>

          <oxd-grid-item v-if="attendanceRecord.previousRecord.note">
            <oxd-input-group :label="$t('attendance.punched_in_note')">
              <oxd-text type="subtitle-2">
                {{ attendanceRecord.previousRecord.note }}
              </oxd-text>
            </oxd-input-group>
          </oxd-grid-item>
        </template>

        <!-- Date Selector -->
        <oxd-grid-item class="--offset-row-2">
          <date-input
            :key="attendanceRecord.time"
            v-model="attendanceRecord.date"
            :label="$t('general.date')"
            :rules="rules.date"
            :disabled="!isEditable || isPunchOutBlockedByDuration"
            required
          />
        </oxd-grid-item>

        <!-- Time  Selector -->
        <oxd-grid-item class="--offset-row-2">
          <oxd-input-field
            v-model="attendanceRecord.time"
            :label="$t('general.time')"
            :disabled="!isEditable || isPunchOutBlockedByDuration"
            :rules="rules.time"
            type="time"
            :placeholder="$t('attendance.hh_mm')"
            required
          />
        </oxd-grid-item>
      </oxd-grid>
    </oxd-form-row>

    <!-- select timezone -->

    <oxd-grid v-if="isTimezoneEditable" :cols="2">
      <oxd-grid-item>
        <timezone-dropdown v-model="attendanceRecord.timezone" required />
      </oxd-grid-item>
    </oxd-grid>

    <!-- Note input -->
    <oxd-form-row>
      <oxd-grid :cols="4" class="orangehrm-full-width-grid">
        <oxd-grid-item class="--span-column-2">
          <oxd-input-field
            v-model="attendanceRecord.note"
            :rules="rules.note"
            :label="$t('general.note')"
            :placeholder="$t('general.type_here')"
            :disabled="isPunchOutBlockedByDuration"
            type="textarea"
          />
        </oxd-grid-item>
      </oxd-grid>
    </oxd-form-row>
    <oxd-divider />

    <oxd-form-actions>
      <required-text />
      <submit-button
        :label="
          !attendanceRecordId ? $t('attendance.in') : $t('attendance.out')
        "
        :disabled="isPunchOutBlockedByDuration"
        @click="primeGeolocationRequest"
      />
    </oxd-form-actions>
  </oxd-form>
</template>

<script>
import {
  required,
  validDateFormat,
  shouldNotExceedCharLength,
  mustNotContainDisallowedNoteLiterals,
} from '@/core/util/validation/rules';
import {
  parseTime,
  parseDate,
  formatTime,
  formatDate,
  guessTimezone,
  setClockInterval,
  getStandardTimezone,
} from '@/core/util/helper/datefns';
import {promiseDebounce} from '@ohrm/oxd';
import useLocale from '@/core/util/composable/useLocale';
import {APIService} from '@ohrm/core/util/services/api.service';
import useDateFormat from '@/core/util/composable/useDateFormat';
import {reloadPage, navigate} from '@/core/util/helper/navigation';
import TimezoneDropdown from '@/orangehrmAttendancePlugin/components/TimezoneDropdown.vue';
import ConfirmationDialog from '@ohrm/components/dialogs/ConfirmationDialog';
import {OxdIcon} from '@ohrm/oxd';

const attendanceRecordModal = {
  date: null,
  time: null,
  note: null,
  timezone: null,
  previousRecord: null,
};

export default {
  name: 'RecordAttendance',
  components: {
    'timezone-dropdown': TimezoneDropdown,
    'oxd-icon': OxdIcon,
    'confirmation-dialog': ConfirmationDialog,
  },
  props: {
    isEditable: {
      type: Boolean,
      default: false,
    },
    isTimezoneEditable: {
      type: Boolean,
      default: false,
    },
    attendanceRecordId: {
      type: Number,
      default: null,
    },
    employeeId: {
      type: Number,
      default: null,
    },
    date: {
      type: String,
      default: null,
    },
    notesDisallowedLiterals: {
      type: Array,
      default: () => [],
    },
    openSessionGuidance: {
      type: Object,
      default: null,
    },
    workWeek: {
      type: Array,
      default: null,
    },
  },
  setup(props) {
    const apiPath = props.employeeId
      ? `/api/v2/attendance/employees/${props.employeeId}/records`
      : '/api/v2/my/attendance/records';
    const http = new APIService(window.appGlobal.baseUrl, apiPath);
    const {jsDateFormat, userDateFormat, timeFormat, jsTimeFormat} =
      useDateFormat();
    const {locale} = useLocale();

    return {
      http,
      locale,
      timeFormat,
      jsTimeFormat,
      jsDateFormat,
      userDateFormat,
    };
  },
  data() {
    return {
      latitude: null,
      longitude: null,
      isLoading: false,
      isClosingStaleSession: false,
      attendanceRecord: {...attendanceRecordModal},
      previousRecordTimezone: null,
      pendingGeolocationRequest: null,
      pendingGeolocationSettled: false,
    };
  },
  computed: {
    bypassConfirmTitle() {
      return this.$t('attendance.open_session_bypass_confirm_title');
    },
    isOutsideWorkWeek() {
      if (!Array.isArray(this.workWeek) || this.attendanceRecordId) {
        return false;
      }
      const parsed = parseDate(this.attendanceRecord.date, 'yyyy-MM-dd');
      if (parsed === null) {
        return false;
      }
      return this.workWeek[parsed.getDay()] === 8;
    },
    bypassConfirmMessage() {
      return this.$t('attendance.open_session_bypass_confirm_message', {
        proxyPunchOutAt: this.openSessionGuidance?.proxyPunchOutAt ?? '',
      });
    },
    rules() {
      return {
        date: [
          required,
          validDateFormat(this.userDateFormat),
          promiseDebounce(this.validateDate, 500),
        ],
        time: [required, promiseDebounce(this.validateDate, 500)],
        note: [
          shouldNotExceedCharLength(250),
          mustNotContainDisallowedNoteLiterals(this.notesDisallowedLiterals),
        ],
      };
    },
    previousAttendanceRecordDate() {
      if (!this.attendanceRecord?.previousRecord) return null;
      return formatDate(
        parseDate(this.attendanceRecord.previousRecord.userDate),
        this.jsDateFormat,
        {locale: this.locale},
      );
    },
    previousAttendanceRecordTime() {
      if (!this.attendanceRecord?.previousRecord) return null;
      return formatTime(
        parseTime(
          this.attendanceRecord.previousRecord.userTime,
          this.timeFormat,
        ),
        this.jsTimeFormat,
      );
    },
    isPunchOutBlockedByDuration() {
      return Boolean(
        this.attendanceRecordId && this.openSessionGuidance?.durationBlocked,
      );
    },
  },
  beforeMount() {
    this.isLoading = true;
    // set default timezone
    if (this.isTimezoneEditable) {
      const tz = guessTimezone();
      this.attendanceRecord.timezone = {
        id: tz.name,
        label: tz.label,
        _name: tz.name,
        _offset: tz.offset,
      };
    }

    // fetch and set attendance record on initial load
    this.setCurrentDateTime()
      .then(() => {
        // then set record date/time every minute
        !this.date &&
          !this.isEditable &&
          setClockInterval(this.setCurrentDateTime, 60000);
        let url = '/api/v2/attendance/records/latest';
        if (this.employeeId) {
          url = `/api/v2/attendance/records/latest?empNumber=${this.employeeId}`;
        }
        return this.attendanceRecordId
          ? this.http.request({method: 'GET', url})
          : null;
      })

      .then((response) => {
        if (response) {
          const {data} = response.data;
          this.attendanceRecord.previousRecord = data.punchIn;
        }
      })
      .then(() => {
        this.previousRecordTimezone = getStandardTimezone(
          this.attendanceRecord.previousRecord?.offset,
        );
      })
      .finally(() => {
        this.isLoading = false;
      });
  },
  methods: {
    canUseGeolocation() {
      return window.isSecureContext && 'geolocation' in navigator;
    },
    isAppleMobileWebKit() {
      const ua = navigator.userAgent || '';
      if (/iPad|iPhone|iPod/.test(ua)) {
        return true;
      }
      // iPadOS 13+ reports a desktop Macintosh user agent.
      return /Macintosh/.test(ua) && navigator.maxTouchPoints > 1;
    },
    isGeolocationError(error) {
      return (
        error &&
        typeof error.code === 'number' &&
        [1, 2, 3].includes(error.code)
      );
    },
    translateOrFallback(key, fallback) {
      const translated = this.$t(key);
      return translated === key ? fallback : translated;
    },
    geolocationErrorMessage(error = null) {
      if (!window.isSecureContext) {
        return this.translateOrFallback(
          'general.geolocation_requires_secure_context',
          'Location requires HTTPS. Clock in/out cannot be saved without location.',
        );
      }
      if (!('geolocation' in navigator)) {
        return this.translateOrFallback(
          'general.geolocation_error',
          'Geolocation is not supported by your browser.',
        );
      }
      if (error && typeof error.code === 'number') {
        if (error.code === 1) {
          const blockedFallback = this.isAppleMobileWebKit()
            ? 'Location could not be read. If this website already has Location set to Allow, open the iPhone Settings app, go to Privacy & Security, Location Services, Safari Websites, choose While Using the App, turn Precise Location on, and try again.'
            : 'Location access is blocked for this site. In Safari, open Settings for This Website and set Location to Allow, then try again.';
          const message = this.translateOrFallback(
            'general.geolocation_permission_denied_safari',
            blockedFallback,
          );
          // Installed databases still have the older sentence, which is
          // wrong when per-website Location is already Allow.
          if (
            this.isAppleMobileWebKit() &&
            message ===
              'Location access is blocked for this site. In Safari, open Settings for This Website and set Location to Allow, then try again.'
          ) {
            return blockedFallback;
          }
          return message;
        }
        if (error.code === 2) {
          return this.translateOrFallback(
            'general.geolocation_position_unavailable',
            'Location could not be determined. Check that Location Services are enabled for your device and browser.',
          );
        }
        if (error.code === 3) {
          return this.translateOrFallback(
            'general.geolocation_unavailable_continue_without',
            'Location could not be retrieved in time. Please try again or check your location settings.',
          );
        }
      }
      return this.translateOrFallback(
        'general.geolocation_error_request',
        'Please allow location before you can clock in/out.',
      );
    },
    showGeolocationError(error = null) {
      this.$toast.warn({
        title: this.translateOrFallback('general.error', 'Error'),
        message: this.geolocationErrorMessage(error),
      });
    },
    getCurrentPositionWithOptions(options) {
      return new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: options.enableHighAccuracy,
          timeout: options.timeout,
          maximumAge: options.maximumAge,
        });
      });
    },
    watchPositionOnce(options) {
      return new Promise((resolve, reject) => {
        let watchId = null;
        let settled = false;
        let deniedTimer = null;
        const timeoutMs = Number(options.timeout) > 0 ? options.timeout : 20000;
        const graceMs =
          Number(options.permissionDeniedGraceMs) > 0
            ? options.permissionDeniedGraceMs
            : 0;
        const finish = (handler, value) => {
          if (settled) {
            return;
          }
          settled = true;
          window.clearTimeout(timer);
          window.clearTimeout(deniedTimer);
          if (watchId !== null) {
            navigator.geolocation.clearWatch(watchId);
          }
          handler(value);
        };
        const timer = window.setTimeout(() => {
          finish(
            reject,
            Object.assign(new Error('Geolocation timeout'), {code: 3}),
          );
        }, timeoutMs);

        watchId = navigator.geolocation.watchPosition(
          (position) => finish(resolve, position),
          (error) => {
            // iOS can emit PERMISSION_DENIED and then deliver a position.
            // Keep the watch open through that gap, including any later
            // unavailable/timeout callbacks.
            if (error?.code === 1 && graceMs > 0) {
              if (deniedTimer !== null) {
                return;
              }
              deniedTimer = window.setTimeout(
                () => finish(reject, error),
                graceMs,
              );
              return;
            }
            if (graceMs > 0 && deniedTimer !== null) {
              return;
            }
            finish(reject, error);
          },
          {
            enableHighAccuracy: options.enableHighAccuracy,
            timeout: timeoutMs,
            maximumAge: options.maximumAge,
          },
        );
      });
    },
    requestPosition(options) {
      if (options.watch) {
        return this.watchPositionOnce(options);
      }
      return this.getCurrentPositionWithOptions(options);
    },
    requestGeolocation() {
      // iOS Safari returns PERMISSION_DENIED (code 1) for a high-accuracy
      // one-shot request even when Settings for This Website → Location is
      // Allow. Start a coarse watchPosition inside the tap, and only treat
      // a second coarse denial as a real block. Other browsers still stop
      // on the first denial.
      const appleMobile = this.isAppleMobileWebKit();
      const attempts = appleMobile
        ? [
            {
              enableHighAccuracy: false,
              timeout: 20000,
              maximumAge: 30000,
              watch: true,
              permissionDeniedGraceMs: 2500,
            },
            {
              enableHighAccuracy: false,
              timeout: 20000,
              maximumAge: 0,
              watch: false,
            },
          ]
        : [
            {
              enableHighAccuracy: true,
              timeout: 15000,
              maximumAge: 60000,
              watch: false,
            },
            {
              enableHighAccuracy: false,
              timeout: 30000,
              maximumAge: 300000,
              watch: false,
            },
          ];

      const tryAttempt = (index, lastError = null) => {
        if (index >= attempts.length) {
          return Promise.reject(lastError ?? new Error('Geolocation failed'));
        }

        return this.requestPosition(attempts[index]).catch((error) => {
          if (!appleMobile && error?.code === 1) {
            throw error;
          }
          return tryAttempt(index + 1, error);
        });
      };

      return tryAttempt(0);
    },
    primeGeolocationRequest() {
      if (!this.canUseGeolocation()) {
        return;
      }
      if (this.pendingGeolocationRequest && !this.pendingGeolocationSettled) {
        return;
      }

      this.pendingGeolocationSettled = false;
      const request = this.requestGeolocation();
      this.pendingGeolocationRequest = request;
      // Validation can fail before getLocation consumes this promise.
      request
        .finally(() => {
          if (this.pendingGeolocationRequest === request) {
            this.pendingGeolocationSettled = true;
          }
        })
        .catch((error) => error);
    },
    consumeGeolocationRequest() {
      const request = this.pendingGeolocationRequest;
      this.pendingGeolocationRequest = null;
      return request ?? this.requestGeolocation();
    },
    onOpenSessionAdjustment() {
      const recordId = this.openSessionGuidance?.recordId;
      if (!recordId) {
        navigate('/attendance/viewMyAttendanceRecord');
        return;
      }
      navigate(`/attendance/editAttendanceAdjustment/${recordId}`);
    },
    async onBypassToClockIn() {
      const recordId = this.openSessionGuidance?.recordId;
      if (!recordId || this.employeeId) {
        return;
      }

      if (!this.canUseGeolocation()) {
        this.showGeolocationError();
        return;
      }

      // Safari only shows the location prompt when geolocation starts in the
      // same user-gesture turn as the click — start before the confirm dialog.
      const geolocationRequest = this.requestGeolocation();

      const confirmation = await this.$refs.bypassConfirmDialog.showDialog();
      if (confirmation !== 'ok') {
        return;
      }

      this.isClosingStaleSession = true;

      try {
        const position = await geolocationRequest;
        const {latitude, longitude} = position.coords;

        await this.setCurrentDateTime();

        const timezone = this.attendanceRecord.timezone
          ? {
              offset: this.attendanceRecord.timezone._offset,
              name: this.attendanceRecord.timezone.id,
            }
          : guessTimezone();

        const closeStaleHttp = new APIService(
          window.appGlobal.baseUrl,
          `/api/v2/my/attendance/records/${recordId}/close-stale`,
        );
        await closeStaleHttp.request({
          method: 'PUT',
          data: {
            coordinates: `${latitude}, ${longitude}`,
            date: this.attendanceRecord.date,
            time: this.attendanceRecord.time,
            timezoneOffset: timezone.offset,
            timezoneName: timezone.name,
            note: '',
          },
        });

        this.$toast.show({
          type: 'success',
          title: this.$t('general.success'),
          message: this.$t('attendance.open_session_bypass_success'),
        });
        reloadPage();
      } catch (error) {
        if (this.isGeolocationError(error)) {
          this.showGeolocationError(error);
          return;
        }
        const apiMessage = error?.response?.data?.error?.message;
        this.$toast.error({
          title: this.translateOrFallback('general.error', 'Error'),
          message:
            apiMessage ||
            this.translateOrFallback(
              'general.unexpected_error',
              'Something went wrong. Please try again.',
            ),
        });
      } finally {
        this.isClosingStaleSession = false;
      }
    },
    getLocation() {
      this.latitude = null;
      this.longitude = null;

      if (!this.canUseGeolocation()) {
        this.showGeolocationError();
        return;
      }

      this.isLoading = true;
      this.consumeGeolocationRequest()
        .then((position) => {
          this.latitude = position.coords.latitude;
          this.longitude = position.coords.longitude;
          this.onSave();
        })
        .catch((error) => {
          this.isLoading = false;
          if (this.isGeolocationError(error)) {
            this.showGeolocationError(error);
            return;
          }
          this.showGeolocationError();
        });
    },
    onSave() {
      if (this.latitude === null || this.longitude === null) {
        return;
      }

      const timezone = guessTimezone();

      this.isLoading = true;
      this.http
        .request({
          method: this.attendanceRecordId ? 'PUT' : 'POST',
          data: {
            coordinates: `${this.latitude}, ${this.longitude}`,
            date: this.attendanceRecord.date,
            time: this.attendanceRecord.time,
            note: this.attendanceRecord.note,
            timezoneOffset:
              this.attendanceRecord.timezone?._offset ?? timezone.offset,
            timezoneName: this.attendanceRecord.timezone?.id ?? timezone.name,
          },
        })
        .then(() => {
          return this.$toast.saveSuccess();
        })
        .then(() => {
          this.employeeId
            ? navigate('/attendance/viewAttendanceRecord', undefined, {
                employeeId: this.employeeId,
                date: this.date,
              })
            : reloadPage();
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    setCurrentDateTime() {
      return new Promise((resolve, reject) => {
        this.http
          .request({method: 'GET', url: '/api/v2/attendance/current-datetime'})
          .then((res) => {
            const {utcDate, utcTime} = res.data.data;
            const currentDate = parseDate(
              `${utcDate} ${utcTime} +00:00`,
              'yyyy-MM-dd HH:mm xxx',
            );
            this.attendanceRecord.date =
              this.date ?? formatDate(currentDate, 'yyyy-MM-dd');
            this.attendanceRecord.time = formatDate(currentDate, 'HH:mm');
            resolve();
          })
          .catch((error) => reject(error));
      });
    },
    validateDate() {
      if (!this.attendanceRecord.date || !this.attendanceRecord.time) {
        return true;
      }
      if (parseDate(this.attendanceRecord.date) === null) {
        return true;
      }
      const tzOffset = (new Date().getTimezoneOffset() / 60) * -1;
      return new Promise((resolve) => {
        this.http
          .request({
            method: 'GET',
            url: `/api/v2/attendance/${
              this.attendanceRecordId ? 'punch-out' : 'punch-in'
            }/overlaps`,
            params: {
              date: this.attendanceRecord.date,
              time: this.attendanceRecord.time,
              timezoneOffset:
                this.attendanceRecord.timezone?._offset ?? tzOffset,
              empNumber: this.employeeId,
            },
            // Prevent triggering response interceptor on 400
            validateStatus: (status) => {
              return (status >= 200 && status < 300) || status == 400;
            },
          })
          .then((res) => {
            const {data, error} = res.data;
            if (error) {
              return resolve(error.message);
            }
            return data.valid === true
              ? resolve(true)
              : resolve(this.$t('attendance.overlapping_records_found'));
          });
      });
    },
  },
};
</script>

<style src="./record-attendance.scss" lang="scss" scoped></style>
